export const BRAIN_TEAM_STORY_PERFORMER_PRESETS = [
  'turtle',
  'person',
  'hiker',
  'cyclist',
  'sailboat',
  'ship',
  'train',
  'car',
  'plane',
  'rocket',
  'fish',
  'whale',
  'bird',
  'dog',
  'camera',
  'compass',
] as const;

export type BrainTeamStoryPerformerPreset = (typeof BRAIN_TEAM_STORY_PERFORMER_PRESETS)[number];

export const BRAIN_TEAM_STORY_PERFORMER_MOTIONS = ['glide', 'walk', 'swim', 'sail', 'fly', 'ride'] as const;

export type BrainTeamStoryPerformerMotion = (typeof BRAIN_TEAM_STORY_PERFORMER_MOTIONS)[number];

/** A bounded presentation character. It can direct how a verified journey is
 * performed, but it never supplies a target, coordinate, route, or evidence. */
export interface BrainTeamStoryPerformer {
  schemaVersion: 1;
  preset: BrainTeamStoryPerformerPreset;
  label: string;
  motion: BrainTeamStoryPerformerMotion;
  accent: string;
}

/** Staff-only authoring command. Omission preserves the current image; null
 * removes it. The server-owned image stamp is deliberately a different type. */
export interface BrainTeamStoryPerformerDraft extends BrainTeamStoryPerformer {
  imageContentId?: string | null;
}

export interface BrainTeamStoryStoredPerformer extends BrainTeamStoryPerformer {
  image?: {
    contentId: string;
    renditionRef: string;
    addedBy: string;
    addedAt: string;
  };
}

export interface BrainTeamStoryPerformerRendition {
  variant: 'medium';
  format: 'jpg' | 'jpeg' | 'png' | 'gif';
  width: number;
  height: number;
  bytes: number;
}

export type BrainTeamStoryPlaybackPerformerImage =
  | {
      refId: string;
      availability: 'available';
      renditionRef: string;
      rendition?: BrainTeamStoryPerformerRendition;
      label?: string;
    }
  | {
      refId: string;
      availability: 'unavailable';
      label?: string;
    };

export interface BrainTeamStoryPlaybackPerformer extends BrainTeamStoryPerformer {
  image?: BrainTeamStoryPlaybackPerformerImage;
}

const PRESET_SET = new Set<string>(BRAIN_TEAM_STORY_PERFORMER_PRESETS);
const MOTION_SET = new Set<string>(BRAIN_TEAM_STORY_PERFORMER_MOTIONS);
const OBJECT_ID = /^[a-f0-9]{24}$/;
const ACCENT = /^#[0-9a-f]{6}$/;
const PERFORMER_REF = /^story-performer:image:[a-f0-9]{16}$/;
const ISO_TIMESTAMP =
  /^\d{4}-\d{2}-\d{2}T(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d(?:\.\d{1,6})?(?:Z|[+-](?:[01]\d|2[0-3]):[0-5]\d)$/;
const RENDITION_FORMATS = new Set(['jpg', 'jpeg', 'png', 'gif']);

const BASE_KEYS = ['schema_version', 'preset', 'label', 'motion', 'accent'] as const;

const hasOnlyKeys = (value: Record<string, unknown>, allowed: readonly string[]): boolean =>
  Object.keys(value).every((key) => allowed.includes(key));

const bounded = (value: unknown, max: number): string | undefined => {
  if (typeof value !== 'string' || value !== value.trim()) return undefined;
  return value.length > 0 && value.length <= max ? value : undefined;
};

function parseBase(
  value: unknown,
  additionalKeys: readonly string[] = [],
): BrainTeamStoryPerformer | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const raw = value as Record<string, unknown>;
  if (!hasOnlyKeys(raw, [...BASE_KEYS, ...additionalKeys])) return undefined;
  const label = bounded(raw.label, 80);
  if (
    raw.schema_version !== 1 ||
    !PRESET_SET.has(String(raw.preset)) ||
    !label ||
    !MOTION_SET.has(String(raw.motion)) ||
    typeof raw.accent !== 'string' ||
    !ACCENT.test(raw.accent)
  ) {
    return undefined;
  }
  return {
    schemaVersion: 1,
    preset: raw.preset as BrainTeamStoryPerformerPreset,
    label,
    motion: raw.motion as BrainTeamStoryPerformerMotion,
    accent: raw.accent,
  };
}

/** Strict five-key consumer parser. A present malformed directive owns and
 * invalidates its containing projection instead of being silently dropped. */
export function parseBrainTeamStoryPerformer(value: unknown): BrainTeamStoryPerformer | undefined {
  return parseBase(value);
}

function validTimestamp(value: unknown): string | undefined {
  const text = bounded(value, 64);
  if (!text || !ISO_TIMESTAMP.test(text) || !Number.isFinite(Date.parse(text))) return undefined;
  const day = text.slice(0, 10);
  return new Date(`${day}T00:00:00.000Z`).toISOString().slice(0, 10) === day ? text : undefined;
}

/** Mirror Asset's authored-image storage-ref guard before a staff stamp is
 * ever handed to SmartImage or URL resolution. */
export function validBrainTeamStoryPerformerRenditionRef(value: unknown): string | undefined {
  const ref = bounded(value, 2048);
  const hasControlCharacter =
    !!ref &&
    Array.from(ref).some((character) => {
      const code = character.charCodeAt(0);
      return code <= 31 || code === 127;
    });
  if (
    !ref ||
    hasControlCharacter ||
    ref.includes(',') ||
    ref.includes('\\') ||
    ref.includes('://') ||
    ref.startsWith('/') ||
    ref.split('/').includes('..')
  ) {
    return undefined;
  }
  return ref;
}

export function parseBrainTeamStoryStoredPerformer(
  value: unknown,
): BrainTeamStoryStoredPerformer | undefined {
  const base = parseBase(value, ['image']);
  if (!base || !value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const raw = value as Record<string, unknown>;
  if (raw.image === undefined) return base;
  if (!raw.image || typeof raw.image !== 'object' || Array.isArray(raw.image)) return undefined;
  const image = raw.image as Record<string, unknown>;
  if (!hasOnlyKeys(image, ['content_id', 'rendition_ref', 'added_by', 'added_at'])) return undefined;
  const contentId = bounded(image.content_id, 24);
  const renditionRef = validBrainTeamStoryPerformerRenditionRef(image.rendition_ref);
  const addedBy = bounded(image.added_by, 128);
  const addedAt = validTimestamp(image.added_at);
  if (!contentId || !OBJECT_ID.test(contentId) || !renditionRef || !addedBy || !addedAt) return undefined;
  return { ...base, image: { contentId, renditionRef, addedBy, addedAt } };
}

function parseRendition(value: unknown): BrainTeamStoryPerformerRendition | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const raw = value as Record<string, unknown>;
  if (!hasOnlyKeys(raw, ['variant', 'format', 'width', 'height', 'bytes'])) return undefined;
  const positiveInteger = (candidate: unknown, max: number): candidate is number =>
    typeof candidate === 'number' && Number.isSafeInteger(candidate) && candidate > 0 && candidate <= max;
  if (
    raw.variant !== 'medium' ||
    !RENDITION_FORMATS.has(String(raw.format)) ||
    !positiveInteger(raw.width, 100_000) ||
    !positiveInteger(raw.height, 100_000) ||
    !positiveInteger(raw.bytes, 64 * 1024 * 1024)
  ) {
    return undefined;
  }
  return {
    variant: 'medium',
    format: raw.format as BrainTeamStoryPerformerRendition['format'],
    width: raw.width,
    height: raw.height,
    bytes: raw.bytes,
  };
}

export function parseBrainTeamStoryPlaybackPerformer(
  value: unknown,
): BrainTeamStoryPlaybackPerformer | undefined {
  const base = parseBase(value, ['image']);
  if (!base || !value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const raw = value as Record<string, unknown>;
  if (raw.image === undefined) return base;
  if (!raw.image || typeof raw.image !== 'object' || Array.isArray(raw.image)) return undefined;
  const image = raw.image as Record<string, unknown>;
  if (!hasOnlyKeys(image, ['ref_id', 'availability', 'rendition_ref', 'rendition', 'label'])) {
    return undefined;
  }
  const refId = bounded(image.ref_id, 192);
  const label = image.label === undefined ? undefined : bounded(image.label, 160);
  if (!refId || !PERFORMER_REF.test(refId) || (image.label !== undefined && !label)) return undefined;
  if (image.availability === 'unavailable') {
    if (image.rendition_ref !== undefined || image.rendition !== undefined) return undefined;
    return { ...base, image: { refId, availability: 'unavailable', ...(label ? { label } : {}) } };
  }
  if (image.availability !== 'available') return undefined;
  const renditionRef = validBrainTeamStoryPerformerRenditionRef(image.rendition_ref);
  const rendition = image.rendition === undefined ? undefined : parseRendition(image.rendition);
  if (!renditionRef || (image.rendition !== undefined && !rendition)) return undefined;
  return {
    ...base,
    image: {
      refId,
      availability: 'available',
      renditionRef,
      ...(rendition ? { rendition } : {}),
      ...(label ? { label } : {}),
    },
  };
}

export function sameBrainTeamStoryPerformer(
  left: BrainTeamStoryPerformer | undefined,
  right: BrainTeamStoryPerformer | undefined,
): boolean {
  if (!left || !right) return left === right;
  return (
    left.schemaVersion === right.schemaVersion &&
    left.preset === right.preset &&
    left.label === right.label &&
    left.motion === right.motion &&
    left.accent === right.accent
  );
}

/** How each preset naturally travels — the motion a fresh performer starts
 *  with, and the motion a picker thumbnail animates so the gallery shows the
 *  character the way the map will move it. */
export const BRAIN_TEAM_STORY_PERFORMER_DEFAULT_MOTION: Record<
  BrainTeamStoryPerformerPreset,
  BrainTeamStoryPerformerMotion
> = {
  turtle: 'walk',
  person: 'walk',
  hiker: 'walk',
  cyclist: 'ride',
  sailboat: 'sail',
  ship: 'sail',
  train: 'ride',
  car: 'ride',
  plane: 'fly',
  rocket: 'fly',
  fish: 'swim',
  whale: 'swim',
  bird: 'fly',
  dog: 'walk',
  camera: 'glide',
  compass: 'glide',
};

export function brainTeamStoryPerformerLabel(preset: BrainTeamStoryPerformerPreset): string {
  return preset.charAt(0).toUpperCase() + preset.slice(1);
}

const PERFORMER_GLYPH: Record<BrainTeamStoryPerformerPreset, string> = {
  turtle: '🐢',
  person: '🧑',
  hiker: '🥾',
  cyclist: '🚴',
  sailboat: '⛵',
  ship: '🚢',
  train: '🚆',
  car: '🚗',
  plane: '✈️',
  rocket: '🚀',
  fish: '🐟',
  whale: '🐋',
  bird: '🐦',
  dog: '🐕',
  camera: '📷',
  compass: '🧭',
};

export function brainTeamStoryPerformerGlyph(preset: BrainTeamStoryPerformerPreset): string {
  return PERFORMER_GLYPH[preset];
}

export function createBrainTeamStoryPerformer(
  preset: BrainTeamStoryPerformerPreset,
  previous?: BrainTeamStoryPerformer | null,
): BrainTeamStoryPerformer {
  return {
    schemaVersion: 1,
    preset,
    label: brainTeamStoryPerformerLabel(preset),
    motion: BRAIN_TEAM_STORY_PERFORMER_DEFAULT_MOTION[preset],
    accent: previous?.accent ?? '#2563eb',
  };
}
